<!-- BflHelpDialogComponent — opened as a nested MatDialog above the Best Fit Finder -->

<div class="bfl-hd-container">

  <!-- Header -->
  <div class="bfl-hd-header">
    <span class="bfl-hd-title">
      <i class="pi pi-star-fill" style="color:#4f46e5"></i>
      Best Fit Finder — How to use
    </span>
    <button class="bfl-hd-close" (click)="close()" title="Close">
      <i class="pi pi-times"></i>
    </button>
  </div>

  <!-- Scrollable body -->
  <div class="bfl-hd-body">

    <!-- Section 1: Search -->
    <div class="bfl-hd-section">
      <div class="bfl-hd-section-title">
        <i class="pi pi-search"></i> Searching
      </div>
      <div class="bfl-hd-section-body">
        <p>Enter a <strong>Check-in</strong> and <strong>Check-out</strong> date (or a range
        start/end in flexible mode), optionally filter by <strong>Bedrooms</strong> or
        <strong>Resort / development</strong>, then click <strong>Find Best Fit</strong>.</p>
        <p>Results scan every available consecutive window for all matching properties
        across that date range and rank them by fit score.</p>
      </div>
    </div>

    <!-- Section 2: Flexible dates -->
    <div class="bfl-hd-section">
      <div class="bfl-hd-section-title">
        <i class="pi pi-calendar-plus"></i> Flexible dates
      </div>
      <div class="bfl-hd-section-body">
        <p>Turn on <strong>Flexible dates</strong> when the guest can arrive on <em>any day</em>
        within a range. Then pick one or more night counts (e.g. 4, 5, 7) using the
        <strong>Num Nights</strong> filter that appears in the search bar.</p>
        <p>The finder will scan every matching consecutive window of those lengths
        and surface the ones that best fill gaps in each property's calendar.</p>
      </div>
    </div>

    <!-- Section 3: Fit score -->
    <div class="bfl-hd-section">
      <div class="bfl-hd-section-title">
        <i class="pi pi-chart-bar"></i> Fit score (1–100)
      </div>
      <div class="bfl-hd-section-body">
        <div class="bfl-hd-badges-row">
          <span class="bfl-hd-badge bfl-hd-badge-best">
            <i class="pi pi-star-fill"></i> Best fit &nbsp;75–100
          </span>
          <span class="bfl-hd-badge bfl-hd-badge-good">
            <i class="pi pi-check-circle"></i> Good fit &nbsp;45–74
          </span>
          <span class="bfl-hd-badge bfl-hd-badge-open">
            <i class="pi pi-calendar"></i> Open &nbsp;1–44
          </span>
        </div>
        <p>The score combines four signals from YieldMax:</p>
        <ul class="bfl-hd-list">
          <li><strong>Gap fill (DPM adjustment)</strong> — how aggressively the algorithm
              discounted this window to fill a calendar gap. Larger connecting discounts
              score higher.</li>
          <li><strong>Revenue quality</strong> — how close to rack rate this window is priced.
              Pricing at or above list rate scores highest.</li>
          <li><strong>Occupancy deviation</strong> — is this property running behind
              its occupancy target? Below-pace properties score higher (more urgent to sell).</li>
          <li><strong>Stay length</strong> — does the booking length meet or exceed the
              property's expected minimum stay?</li>
        </ul>
        <p><strong>Connecting bonus:</strong> if the window is back-to-back with an existing
        booking on both sides (+15 pts) or one side (+10 pts), a bonus is added — these windows
        eliminate calendar gaps entirely and are the most valuable to sell.</p>
        <p><strong>Wasted-gap penalty:</strong> if accepting this booking would leave a gap
        before or after that is too short to re-sell (shorter than the property's minimum stay),
        up to 10 pts are deducted on each side.</p>
        <p>While pricing loads, the score falls back to a client-side
        <strong>gap score</strong> (0–2) based on whether bookings exist immediately
        before/after the window.</p>
      </div>
    </div>

    <!-- Section 4: Gap columns -->
    <div class="bfl-hd-section">
      <div class="bfl-hd-section-title">
        <i class="pi pi-arrows-h"></i> Gap columns (←Gap / Gap→)
      </div>
      <div class="bfl-hd-section-body">
        <p>These show how many <em>consecutive empty nights</em> exist immediately
        before check-in and after check-out on that property.</p>
        <div class="bfl-hd-gap-legend">
          <span class="bfl-hd-gap bfl-hd-gap-z">0n</span>
          &nbsp;Sold tight — zero orphan nights. Best for the owner.
        </div>
        <div class="bfl-hd-gap-legend">
          <span class="bfl-hd-gap bfl-hd-gap-s">1–2n</span>
          &nbsp;Small gap — a short window is still open.
        </div>
        <div class="bfl-hd-gap-legend">
          <span class="bfl-hd-gap bfl-hd-gap-l">3n+</span>
          &nbsp;Large gap — longer orphan period on either side.
        </div>
      </div>
    </div>

    <!-- Section 5: Price column -->
    <div class="bfl-hd-section">
      <div class="bfl-hd-section-title">
        <i class="pi pi-dollar"></i> Price column
      </div>
      <div class="bfl-hd-section-body">
        <p><strong>Total w/tax</strong> — the full grand total including taxes and fees,
        pulled from the live pricing engine after the search. The smaller
        <strong>/nt</strong> value is the average per-night rate for that window.</p>
        <p>While pricing loads you'll see a spinner. If pricing fails, an "unavailable"
        note appears.</p>
      </div>
    </div>

    <!-- Section 6: Building a quote -->
    <div class="bfl-hd-section">
      <div class="bfl-hd-section-title">
        <i class="pi pi-file-edit"></i> Building a quote
      </div>
      <div class="bfl-hd-section-body">
        <p><strong>Single window:</strong> click the <em>Quote</em> button on any row.
        The quoting dialog opens pre-filled with that property and dates.</p>
        <p><strong>Multiple windows:</strong> tick the checkbox on each row you want
        to compare, then click <em>Build Quote (N)</em> in the toolbar. All selected
        windows are passed to the quote dialog for comparison.</p>
      </div>
    </div>

    <!-- Section 7: Sorting & filtering -->
    <div class="bfl-hd-section">
      <div class="bfl-hd-section-title">
        <i class="pi pi-filter"></i> Sorting &amp; filtering
      </div>
      <div class="bfl-hd-section-body">
        <ul class="bfl-hd-list">
          <li>Click any <strong>column header</strong> to sort ascending or descending.</li>
          <li>Use the <strong>dropdown filters</strong> in the second header row to narrow
              by Fit level, Bedrooms, Bathrooms, View type, or Night count.</li>
          <li>Type in the <strong>Property</strong> filter to search by property code
              or development name.</li>
          <li>Use the <strong>global search box</strong> (top left of the toolbar) to
              search across all columns at once.</li>
          <li>Click <strong>Clear filters</strong> to reset everything.</li>
        </ul>
      </div>
    </div>

  </div><!-- /bfl-hd-body -->

  <!-- Footer -->
  <div class="bfl-hd-footer">
    <button class="bfl-hd-close-btn" (click)="close()">
      <i class="pi pi-times"></i> Close
    </button>
  </div>

</div>
